import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Users, Heart, UserPlus, Trash2, Star, X } from "lucide-react";
import { api, formatApiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function statusBadge(s) {
  const map = {
    confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    completed: "bg-blue-50 text-blue-800 border-blue-200",
    cancelled: "bg-rose-50 text-rose-800 border-rose-200",
    no_show: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return `badge border ${map[s] || map.pending}`;
}

export default function PatientDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("upcoming");
  const [appts, setAppts] = useState([]);
  const [profile, setProfile] = useState({ family_members: [], favorite_doctor_ids: [] });
  const [newFam, setNewFam] = useState({ name: "", relation: "Spouse", age: "", gender: "male", phone: "" });
  const [reviewing, setReviewing] = useState(null); // appointment
  const [review, setReview] = useState({ rating: 5, comment: "" });

  const load = async () => {
    const [a, p] = await Promise.all([api.get("/appointments/my"), api.get("/patient/me")]);
    setAppts(a.data);
    setProfile(p.data || { family_members: [], favorite_doctor_ids: [] });
  };
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = appts.filter((a) => a.date >= today && ["confirmed", "pending"].includes(a.status));
  const past = appts.filter((a) => a.date < today || ["completed", "cancelled", "no_show"].includes(a.status));

  const cancel = async (id) => {
    try {
      await api.patch(`/appointments/${id}/status`, null, { params: { status: "cancelled" } });
      toast.success("Appointment cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addFamily = async (e) => {
    e.preventDefault();
    try {
      await api.post("/patient/family", { ...newFam, age: Number(newFam.age) });
      setNewFam({ name: "", relation: "Spouse", age: "", gender: "male", phone: "" });
      toast.success("Family member added");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delFamily = async (mid) => {
    await api.delete(`/patient/family/${mid}`);
    toast.success("Removed");
    load();
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { appointment_id: reviewing.id, rating: review.rating, comment: review.comment });
      toast.success("Review submitted. Thank you!");
      setReviewing(null); setReview({ rating: 5, comment: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ApptCard = ({ a }) => (
    <div data-testid={`appt-${a.booking_id}`} className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] font-semibold tracking-widest text-slate-500">#{a.booking_id}</div>
          <div className="mt-1 text-lg font-bold text-slate-900">{a.doctor_name}</div>
          <div className="text-xs font-medium text-blue-700">{a.doctor_specialty}</div>
        </div>
        <span className={statusBadge(a.status)}>{a.status.replace("_", " ")}</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700">
        <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-slate-400" />{a.date}</span>
        <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" />{a.time}</span>
        <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" />{a.clinic_name}</span>
        <span className="font-semibold text-slate-900">{formatINR(a.fee)} • {a.payment_method === "pay_at_clinic" ? "Pay at Clinic" : "Online"}</span>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {a.status === "confirmed" && a.date >= today && (
          <button data-testid={`cancel-${a.booking_id}`} onClick={() => cancel(a.id)} className="btn-outline !py-2 !text-sm">Cancel</button>
        )}
        {a.status === "completed" && (
          <button data-testid={`review-${a.booking_id}`} onClick={() => setReviewing(a)} className="btn-secondary !py-2 !text-sm">
            <Star className="h-4 w-4" /> Write Review
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Hi, {user?.name} 👋</h1>
        <p className="mt-1 text-sm text-slate-600">Manage your appointments and family members here.</p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200">
        {[
          { id: "upcoming", label: `Upcoming (${upcoming.length})` },
          { id: "past", label: `Past (${past.length})` },
          { id: "family", label: `Family (${profile.family_members?.length || 0})` },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`relative px-4 py-3 text-sm font-semibold ${tab === t.id ? "text-blue-700" : "text-slate-500 hover:text-slate-700"}`}
          >
            {t.label}
            {tab === t.id && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-blue-700" />}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "upcoming" && (
          upcoming.length ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{upcoming.map((a) => <ApptCard key={a.id} a={a} />)}</div>
          ) : (
            <div className="card p-10 text-center">
              <Calendar className="mx-auto h-10 w-10 text-slate-300" />
              <div className="mt-3 font-bold text-slate-900">No upcoming appointments</div>
              <Link to="/doctors" className="btn-primary mt-4">Book your first appointment</Link>
            </div>
          )
        )}
        {tab === "past" && (
          past.length ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{past.map((a) => <ApptCard key={a.id} a={a} />)}</div>
          ) : <div className="card p-10 text-center text-sm text-slate-500">No past appointments.</div>
        )}
        {tab === "family" && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_1fr]">
            <div className="card p-6">
              <div className="flex items-center gap-2 text-slate-900"><UserPlus className="h-5 w-5 text-teal-600" /><h3 className="font-bold">Add Family Member</h3></div>
              <form onSubmit={addFamily} className="mt-4 space-y-3">
                <input data-testid="fam-name" required className="input-field" placeholder="Name" value={newFam.name} onChange={(e) => setNewFam({ ...newFam, name: e.target.value })} />
                <select data-testid="fam-relation" className="input-field" value={newFam.relation} onChange={(e) => setNewFam({ ...newFam, relation: e.target.value })}>
                  {["Spouse", "Child", "Parent", "Sibling", "Other"].map((r) => <option key={r}>{r}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="fam-age" required type="number" className="input-field" placeholder="Age" value={newFam.age} onChange={(e) => setNewFam({ ...newFam, age: e.target.value })} />
                  <select data-testid="fam-gender" className="input-field" value={newFam.gender} onChange={(e) => setNewFam({ ...newFam, gender: e.target.value })}>
                    <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                  </select>
                </div>
                <input data-testid="fam-phone" className="input-field" placeholder="Phone (optional)" value={newFam.phone} onChange={(e) => setNewFam({ ...newFam, phone: e.target.value })} />
                <button data-testid="fam-submit" className="btn-primary w-full">Add Member</button>
              </form>
            </div>
            <div className="space-y-3">
              {(profile.family_members || []).length === 0 && <div className="card p-10 text-center text-sm text-slate-500"><Users className="mx-auto h-8 w-8 text-slate-300" /><div className="mt-2">No family members added yet.</div></div>}
              {(profile.family_members || []).map((m) => (
                <div key={m.id} className="card flex items-center justify-between p-4">
                  <div>
                    <div className="font-semibold text-slate-900">{m.name}</div>
                    <div className="text-xs text-slate-500">{m.relation} • {m.age} yrs • {m.gender}</div>
                  </div>
                  <button data-testid={`del-fam-${m.id}`} onClick={() => delFamily(m.id)} className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {reviewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setReviewing(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Review {reviewing.doctor_name}</h3>
              <button onClick={() => setReviewing(null)} className="rounded-full p-2 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 flex items-center gap-1">
              {[1,2,3,4,5].map((n) => (
                <button key={n} data-testid={`star-${n}`} onClick={() => setReview({ ...review, rating: n })}>
                  <Star className={`h-7 w-7 ${n <= review.rating ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
            <textarea data-testid="review-comment" rows={4} className="input-field mt-4" placeholder="Share your experience…" value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} />
            <button data-testid="review-submit" onClick={submitReview} className="btn-primary mt-4 w-full">Submit Review</button>
          </div>
        </div>
      )}
    </div>
  );
}
